import { findQuarticRoots } from './quartic';

// Global scale deteremining stuff

var triangularMemo = {};

function triangularAB(
    a: number,
    b: number,
    mode: number,
    SCALE: number,
): Distribution {
    const xValues = setupXVector(a, b, SCALE);

    const functionVector: number[] = [];
    for (var i = 0; i < xValues.length; i++) {
        functionVector.push(findTriangleValue(xValues[i], a, b, mode));
    }
    //Normalize
    var f = [];
    const functionSum = functionVector.reduce((a, b) => a + b, 0);
    for (var i = 0; i < functionVector.length; i++) {
        f.push(functionVector[i] / functionSum);
    }
    return { a: a, b: b, values: f, symetrical: false, scale: SCALE };
}

function findCDF(distribution: Distribution): number[] {
    let cdf = [];
    let sum = 0;
    for (let i = 0; i < distribution.values.length; i++) {
        sum += distribution.values[i];
        cdf.push(sum);
    }
    return cdf;
}
export function triangular(
    p10: number,
    p90: number,
    value: number,
    SCALE: number,
): Distribution {
    let sym = p10 - value == value - p90;
    let cacheString = JSON.stringify({ p10, p90, value, SCALE });
    if (triangularMemo[cacheString]) {
        return triangularMemo[cacheString];
    }
    const AnB = findABfromQuantiles(value, p10, p90, SCALE);
    const xValues = setupXVector(AnB.a, AnB.b, SCALE);

    const functionVector: number[] = [];
    for (var i = 0; i < xValues.length; i++) {
        functionVector.push(findTriangleValue(xValues[i], AnB.a, AnB.b, value));
    }
    //Normalize
    var f = [];
    const functionSum = functionVector.reduce((a, b) => a + b, 0);
    for (var i = 0; i < functionVector.length; i++) {
        f.push(functionVector[i] / functionSum);
    }
    triangularMemo[cacheString] = {
        a: AnB.a,
        b: AnB.b,
        values: f,
        symetrical: sym,
        scale: SCALE,
    };
    return { a: AnB.a, b: AnB.b, values: f, symetrical: sym, scale: SCALE };
}

export function constant(value: number): Distribution {
    return { a: value, b: value, values: [1], symetrical: true, scale: 1 };
}

export function uniform(p10: number, p90: number, SCALE: number): Distribution {
    // Convert p10 and p90 to a,b:
    let a = p10 - (p90 - p10) / 8;
    let b = p90 + (p90 - p10) / 8;

    // Ensure a and b are multiplums of the SCALE parameter
    if (a % SCALE > 0) {
        a = a - (a % SCALE);
    } else if (a % SCALE < 0) {
        a = a - SCALE - (a % SCALE);
    }

    if (b % SCALE > 0) {
        b = b + SCALE - (b % SCALE);
    } else if (b % SCALE < 0) {
        b = b - (b % SCALE);
    }
    let yf: number = 1 / (b - a);
    let x_tmp = setupXVector(a, b, SCALE);
    let n: number = x_tmp.length;

    let functionVector: number[] = [];
    for (var i = 0; i < n; i++) {
        functionVector.push(yf);
    }
    // Normalize
    let f = [];
    const functionSum = functionVector.reduce((a, b) => a + b, 0);
    for (var i = 0; i < functionVector.length; i++) {
        f.push(functionVector[i] / functionSum);
    }
    return { a: a, b: b, values: f, symetrical: true, scale: SCALE };
}

function findTriangleValue(
    xValue: number,
    a: number,
    b: number,
    value: number,
): number {
    if (xValue < a) return 0.0;
    else if (a <= xValue && xValue < value)
        return (2 * (xValue - a)) / ((b - a) * (value - a));
    else if (xValue == value) return 2 / (b - a);
    else if (value < xValue && xValue <= b)
        return (2 * (b - xValue)) / ((b - a) * (b - value));
    else return 0.0;
}

function findABfromQuantiles(
    value: number,
    p10: number,
    p90: number,
    SCALE: number,
): { a: number; b: number } {

    const symmetric = p10 - value == value - p90;
    if (symmetric) {
        return findSymmetricABfromQuantiles(value, p10, p90, SCALE);
    } else { 
        return findAsymmetricABfromQuantiles(value, p10, p90, SCALE);
    }
}

function findSymmetricABfromQuantiles(
    value: number,
    p10: number,
    p90: number,
    SCALE: number,
): { a: number; b: number } {
    const k = (Math.sqrt(5) + 1) / 4;
    let a = p10 - k * (value - p10);
    let b = p90 + k * (p90 - value);

    if (a % SCALE > 0) {
        a = a - (a % SCALE);
    } else if (a % SCALE < 0) {
        a = a - SCALE - (a % SCALE);
    }

    if (b % SCALE > 0) {
        b = b + SCALE - (b % SCALE);
    } else if (b % SCALE < 0) {
        b = b - (b % SCALE);
    }

    return { a, b };
}

function findAsymmetricABfromQuantiles(
    value: number,
    p10: number,
    p90: number,
    SCALE: number,
): { a: number; b: number } {
    const mode = value;
    const quantileConstant = (p90 - mode) / (mode - p10);
    const k4 = 72;
    const k3 = -2 * (161 + 9 * quantileConstant);
    const k2 = 521 + 40 * quantileConstant + quantileConstant ** 2; //verify this
    const k1 = -(360 + 20 * quantileConstant);
    const k0 = 90;
    const k = [k4, k3, k2, k1, k0];
    const roots = findQuarticRoots(k4, k3, k2, k1, k0);
    if (roots.length == 0) {
        return findSymmetricABfromQuantiles(value, p10, p90, SCALE);
    }
    for (let root of roots) {
        const a = mode - root * (mode - p10);
        const b = a + (10 * (p10 - a) ** 2) / (mode - a);
        const t = triangularAB(a, b, mode, SCALE);
        const Ft = findCDF(t);
        if (
            a < p10 &&
            b > p90 &&
            Math.round(Ft[Math.round((-a + p10) / SCALE)] * 10) / 10 == 0.1 &&
            Math.round(Ft[Math.round((-a + p90) / SCALE)] * 10) / 10 == 0.9
        ) {
            return { a, b };
        }
    }
}

function setupXVector(a: number, b: number, SCALE: number): number[] {
    let n = Math.floor((b - a) / SCALE) + 1;
    let x: number[] = [];
    for (var i = 0; i < n; i++) {
        x.push(a + SCALE * i);
    }
    if (x[x.length - 1] < b) {
        x.push(x[x.length - 1] + SCALE);
    }
    return x;
}

var mixtureMemo = {};
export function mixture(
    distributions: WeightedDistribution[],
    SCALE: number,
): Distribution {
    let cacheString = JSON.stringify({ distributions, SCALE });
    if (mixtureMemo[cacheString]) {
        return mixtureMemo[cacheString];
    }
    if (distributions.length == 1) {
        return distributions[0].distribution;
    }

    if (distributions.reduce((a, b) => a + b.weight, 0) != 1) {
    }

    // Find the new interval for the mixed distribution
    // This will be an interval that encloses both the other distribution intervals

    let a_values: number[] = [];
    let b_values: number[] = [];

    distributions.map((d) => {
        a_values.push(d.distribution.a);
        b_values.push(d.distribution.b);
    });

    const a_new = arrayMin(a_values);
    const b_new = arrayMax(b_values);

    //distributions = ensureConstantsAreMultiplumsOfScale

    const new_x = setupXVector(a_new, b_new, SCALE);
    let new_f: number[] = [];

    // Each point in the new distribution is a weighted sum of all the input distributions
    // find_value returns 0.0 if x is outside the interval for the distribution

    for (let i = 0; i < new_x.length; i++) {
        let s = 0;
        for (let j = 0; j < distributions.length; j++) {
            s =
                s +
                findValue(distributions[j].distribution, new_x[i], SCALE) *
                    distributions[j].weight;
        }
        if (isNaN(s)) {
            new_f.push(0);
        } else {
            new_f.push(s);
        }
    }
    let sym = distributions.every((val) => val.distribution.symetrical);

    mixtureMemo[cacheString] = {
        a: a_new,
        b: b_new,
        values: new_f,
        symetrical: sym,
        scale: SCALE,
    };
    return { a: a_new, b: b_new, values: new_f, symetrical: sym, scale: SCALE };
}

let convolutionCache = {};
export function convolute(
    distribution1: Distribution,
    distribution2: Distribution,
    SCALE: number,
): Distribution {
    let cacheString = JSON.stringify({ distribution1, distribution2, SCALE });
    if (convolutionCache[cacheString]) {
        return convolutionCache[cacheString];
    }

    if (distribution1.values.length == 1 && distribution2.values.length == 1) {
        return {
            a: distribution1.a + distribution2.a,
            b: distribution1.a + distribution2.a,
            values: [1],
            symetrical: distribution1.symetrical && distribution2.symetrical,
            scale: SCALE,
        };
    }

    let f1 = distribution1.values;
    let f2 = distribution2.values;

    let new_f: number[] = new Array(f1.length + f2.length - 1);

    for (let i = 0; i < new_f.length; i++) {
        new_f[i] = 0;
        for (let j = 0; j < f1.length; j++) {
            if (j >= f1.length) {
                continue;
            }
            if (i - j < 0) {
                continue;
            }
            if (i - j >= f2.length) {
                continue;
            }
            new_f[i] += f1[j] * f2[i - j];
        }
    }
    const sum = new_f.reduce((a, b) => a + b, 0);
    const normalized_values = new_f.map((v) => v / sum);

    const new_a = distribution1.a + distribution2.a;
    const new_b = distribution1.b + distribution2.b;
    const x_new = setupXVector(new_a, new_b, SCALE);
    let res = {
        a: arrayMin(x_new),
        b: arrayMax(x_new),
        values: normalized_values,
        symetrical: distribution1.symetrical && distribution2.symetrical,
        scale: SCALE,
    };
    convolutionCache[cacheString] = res;
    return res;
}

function arrayMin(arr) {
    var len = arr.length,
        min = Infinity;
    while (len--) {
        if (arr[len] < min) {
            min = arr[len];
        }
    }
    return min;
}

function arrayMax(arr) {
    var len = arr.length,
        max = -Infinity;
    while (len--) {
        if (arr[len] > max) {
            max = arr[len];
        }
    }
    return max;
}

export function convoluteMultipleDistributions(
    distributions: Distribution[],
    SCALE,
): Distribution {
    if (distributions.length < 2) {
        return distributions[0];
    }
    var newCovolution = convolute(
        distributions.pop(),
        distributions.pop(),
        SCALE,
    );
    return convoluteMultipleDistributions(
        distributions.concat(newCovolution),
        SCALE,
    );
}

function findValue(
    distribution: Distribution,
    value: number,
    SCALE: number,
): number {
    if (distribution.values.length == 1) {
        if (value == distribution.a) {
            return 1;
        }
        if (value > distribution.a && value < distribution.b + SCALE) {
            return 1 - (value - distribution.a) / SCALE;
        }
        if (value > distribution.a - SCALE && value < distribution.b) {
            return 1 - (distribution.a - value) / SCALE;
        } else {
            return 0;
        }
    } else {
        if (value < distribution.a) {
            return 0;
        }
        if (value > distribution.b) {
            return 0;
        }
        let index = Math.floor((value - distribution.a) / SCALE);
        return distribution.values[index];
    }
}

interface Distribution {
    a: number;
    b: number;
    values: number[];
    symetrical: boolean;
    scale: number;
}

interface WeightedDistribution {
    distribution: Distribution;
    weight: number;
}



// WEBPACK FOOTER //
// ./scripts/components/convolution.ts